<template>
	<div>
		<v-data-table
			:headers="headers_local"
			:items="items_local"
			:page.sync="page"
			:items-per-page="countPerpage"
			hide-default-header
			hide-default-footer
			@click:row="onClickLeaveItem($event)"
		>
			<template v-if="!$vuetify.breakpoint.smAndDown" v-slot:header="{ props: { headers } }">
				<thead>
					<v-draggable v-model="headers_local" tag="tr">
						<th 
							v-for="item in headers" 
							:key="item.value" 
							class="header-th" 
							@mouseover="isHover = item.value"
							@mouseout="isHover = ''">
							<div class="header-item" v-if="item.showable">
								<div class="header-item-sub">
									<div class="header-item-text">{{ item.text }}</div>
									<SortUpDown 
										v-if="item.type === 'sort'" 
										class="icon icon-sort-up-down" 
										:selected="isHover === item.value" 
										:selected_color="color" 
										@click="onSort(item.value)"/>
								</div>
								<v-menu offset-y nudge-left="200" >
									<template v-slot:activator="{ on, attrs }">
										<div class="icon-wrap" v-bind="attrs" v-on="on">
											<ShowSearch 
												class="icon" 
												v-if="item.searchable" 
												:selected="isHover === item.value" 
												:selected_color="color"/>
										</div>
									</template>
									<div class="search-box">
										<v-text-field
											placeholder="Search Title..."
											outlined
											dense
											hide-details="auto"
											color="#00AAFD" 
											:value="current_key === item.value ? searchValue : ''"
											@input="searchValue = $event"
											@click.stop
										/>
										<div class="search-box-button">
											<div @click.stop="onClean">CLEAR</div>
											<div @click="onSearch(item.value)">SEARCH</div>
										</div>
									</div>
								</v-menu>
								<v-menu offset-y nudge-left="200" min-width="100px" :close-on-content-click="false">
									<template v-slot:activator="{ on, attrs }">
										<div class="icon-wrap" v-bind="attrs" v-on="on">
											<FirstList 
												v-if="item.value === 'type_desc'" 
												class="icon icon-sort-up-down" 
												:selected="isHover === item.value" 
												:selected_color="color"/>
										</div>
									</template>
									<div v-for="type in event_types" :key="type.id">
										<v-checkbox
											class="filter-box mt-0"
											v-model="select_type"
											:label="type.desc"
											:value="type.id"
											color="info"
											dense
											hide-details>
										</v-checkbox>
									</div>
								</v-menu>
								<v-menu offset-y nudge-left="100" min-width="120px" :close-on-content-click="false">
									<template v-slot:activator="{ on, attrs }">
										<div class="icon-wrap" v-bind="attrs" v-on="on">
											<FirstList 
												v-if="item.value === 'status_desc'" 
												class="icon icon-sort-up-down" 
												:selected="isHover === item.value" 
												:selected_color="color"/>
										</div>
									</template>
									<div v-for="status in statusses" :key="status.id">
										<v-checkbox
											class="filter-box mt-0"
											v-model="select_status"
											:label="status.desc"
											:value="status.id"
											color="info"
											dense
											hide-details>
										</v-checkbox>
									</div>
								</v-menu>
							</div>
						</th>
					</v-draggable> 
				</thead>
			</template>
			<template v-slot:[`item.start_date`]="{ item }">			
				<span :class="checkValid(item)? 'text-able':'text-disable'">{{ formatDate(item.start_date) }}</span>
			</template>
			<template v-slot:[`item.end_date`]="{ item }">			
				<span :class="checkValid(item)? 'text-able':'text-disable'">{{ formatDate(item.end_date) }}</span>
			</template>
			<template v-slot:[`item.description`]="{ item }">
				<span :class="checkValid(item)? 'text-able':'text-disable'" v-html="item.description"></span>
			</template>
			<template v-slot:[`item.duration`]="{ item }">
				<span :class="checkValid(item)? 'text-able':'text-disable'">{{ formatDuration(item.duration) }}</span>
			</template>
			<template v-slot:[`item.type_desc`]="{ item }">
				<span :class="checkValid(item)? 'text-able':'text-disable'">{{ item.type_desc }}</span>
			</template>
			<template v-slot:[`item.status_desc`]="{ item }">
				<span :class="checkValid(item)? 'text-able':'text-disable'">{{ item.status_desc }}</span>
			</template>
		</v-data-table>
		<div class="pagination">
			<v-pagination
				v-model="page"
				:color="color"
				:length="getPaginationTotal()"
				:total-visible="$vuetify.breakpoint.xs ? 3: 5"
				circle
			></v-pagination>
			<div class="pagination-text">show</div>
			<div class="pagination-text-page">{{ countPerpage}}/page</div>
			<v-menu offset-y>
				<template v-slot:activator="{ on, attrs }">
					<div v-bind="attrs" v-on="on"><Down class="down"/></div>
				</template>
				<v-list>
					<v-list-item-title v-for="(pageOption, index) in pageOptions" :key="index" class="option-text" @click="onClickOption(pageOption)">
						{{pageOption}}
					</v-list-item-title>
				</v-list>
			</v-menu>
		</div>
	</div>
</template>
<script>
import SortUpDown from '@/components/icons/sort_up_down';
import ShowSearch from '@/components/icons/show_search';
import FirstList from "@/components/icons/first_list";
import Down from '@/components/icons/down';

import config from "@/config.js";
import moment from 'moment';
import { metaMixin } from "@/mixins/events/metaMixin";

const FONT_COLOR = config.font_color;
const PENDING_STATUS = 0; /* 0=pending, 1=approved, 2=paid */

/** If selected the user can manage public holidays */
const DPubHol = 'DPubHol';

export default {
	name: 'LeaveTable',
	mixins: [ metaMixin ],
	props: {
		color: {
			type: String,
			default: ""
		},
		items: {
			type: Array,
			default: () => []
		},
	},
	components: {
		SortUpDown, 
		ShowSearch,
		FirstList,
		Down
	},
	data() {
		return {
			headers_local: config.leave_head,
			items_local: [],
			items_local_original: [],
			searchValue: '',
			sortByAscent: false,
			isHover: '',
			current_key: '',
			page: 1,
			countPerpage: this.$vuetify.breakpoint.xs ? 10 : config.countPerpage,
			pageOptions: config.pageOptions,
			statusses: [],
			event_types: [],
			select_type: [],
			select_status: [],
			pending_status: PENDING_STATUS,
		}
	},
	async created() {
		await this.load_meta_data();
		this.items_local = this.items;
		this.items_local_original = this.items;
	},
	watch: {
		items() {
			this.items_local = this.items;
			this.items_local_original = this.items;
		},
		event_types() {
			this.select_type = this.event_types.map(type => { return type.id});
		},
		statusses() {
			this.select_status = this.statusses.map(status => { return status.id });
		},
		select_type() {
			this.items_local = this.items_local_original.filter(item => {
				return this.select_type.includes(item.event_type.id);
			});
		},
		select_status() {
			this.items_local = this.items_local_original.filter(item => {
				return this.select_status.includes(item.status);
			});
		}
	},
	computed: {
		pubHolPrivilege() {
			let isPubHolPrivilegeTmp = false;
			if (this.$store.state.privileges && this.$store.state.privileges.length !== 0 && this.$store.state.privileges.includes(DPubHol)) {
				isPubHolPrivilegeTmp = true;
			}
			return isPubHolPrivilegeTmp;
		},
	},
	methods: {
		onClickLeaveItem(leave) {
			if (this.checkValid(leave)) {
				this.$emit('row-click', leave.id);
			}
		},
		formatDate(date) {
			if(!date) return null;
			return moment(date).format('DD MMM YYYY');
		},
		formatDuration(minutes) {
			if (minutes == 0) {
				return '0h';
			}
			
			let hours = Math.floor(minutes / 60.0);
			let mins = minutes - (hours * 60.0);

			let fmt = '';
			if (hours > 0) {
				fmt += hours + 'h';
				if (mins > 0) {
					fmt += ' ';
				}
			}

			if (mins > 0) {
				fmt += mins + 'm';
			}

			return fmt;
		},
		checkValid(item) {
			let is_able = true;

			if (item.leave_type === config.public_holiday_type) {
				if (this.pubHolPrivilege) {
					is_able = item.status === this.pending_status ? true : false;
				} else {
					is_able = false;
				}
				
			} else {
				is_able = item.status === this.pending_status ? true : false;
			}
		
			return is_able;
		},
		getPaginationTotal() {
			return Math.ceil(this.items_local.length / this.countPerpage);
		},
		onClickOption(option) {
			this.countPerpage = option;
		},
		onSearch(key) {
			if (this.searchValue.length === 0) return;

			this.current_key = key;
				
			const arr_filter = this._filterKey(this.items_local_original, key, this.searchValue);
			const arr_replace = JSON.parse(JSON.stringify(arr_filter));

			// to hightlight the kewwords
			const regex = new RegExp(this.searchValue, 'gi');
			arr_replace.forEach(arr => {
				if (arr[key]) {
					if (key === 'code') {
						arr[key].code = arr[key].code.replace(regex, (keyword) => {
							return `<font color="${FONT_COLOR}">${keyword}</font>`;
						});
					} else {
						if ( typeof(arr[key]) === 'object' ) {
							arr[key].display_name = arr[key].display_name.replace(regex, (keyword) => {
								return `<font color="${FONT_COLOR}">${keyword}</font>`;
							});
						} else {
							arr[key] = arr[key].replace(regex, (keyword) => {
								return `<font color="${FONT_COLOR}">${keyword}</font>`;
							});
						}
					}
				}
			});
			this.items_local = arr_replace;
		},
		onClean() {
			this.searchValue = '';
			this.items_local = this.items_local_original;
		},
		onSort(key) {
			this.sortByAscent = !this.sortByAscent;

			if (key === 'duration') {
				this.sortByAscent
					? this._sort_ascent_number(this.items_local, key)
					: this._sort_descent_number(this.items_local, key);
			} else {
				this.sortByAscent
					? this._sort_ascent(this.items_local, key)
					: this._sort_descent(this.items_local, key);
			}	
		},
		_sort_ascent(arr, key) {
			arr.sort((a, b) => {
				let valueA = '';
				let valueB = '';
				if (a[key]) {
					valueA = typeof(a[key]) === 'object' ? a[key].display_name.toUpperCase() : a[key].toUpperCase();
				}

				if (b[key]) {
					valueB = typeof(b[key]) === 'object' ? b[key].display_name.toUpperCase() : b[key].toUpperCase();
				}

				if (valueA < valueB) {
					return -1;
				}
				if (valueA > valueB) {
					return 1;
				}

				return 0;
			});
		},
		_sort_descent(arr, key) {
			arr.sort((a, b) => {
				let valueA = '';
				let valueB = '';

				if (a[key]) {
					valueA = typeof(a[key]) === 'object' ? a[key].display_name.toUpperCase() : a[key].toUpperCase();
				} 

				if (b[key]) {
					valueB = typeof(b[key]) === 'object' ? b[key].display_name.toUpperCase() : b[key].toUpperCase();
				}

				if (valueA > valueB) {
					return -1;
				}
				if (valueA < valueB) {
					return 1;
				}

				return 0;
			});
		},
		_sort_ascent_number(arr, key) {
			arr.sort((a, b) => {
				return a[key] - b[key];
			});
		},
		_sort_descent_number(arr, key) {
			arr.sort((a, b) => {
				return b[key] - a[key];
			});
		},
		_filterKey(arr, key, filter) {
			let fliter_Arr = [];
			const filter_UpperCase = filter.toUpperCase();
			fliter_Arr = arr.filter(a => {
				let A = '';
				if (a[key]) {
					if (key === 'code') {
						A = typeof(a[key]) === 'object' ? a[key].code.toUpperCase() : a[key].toUpperCase();
					} else {
						A = typeof(a[key]) === 'object' ? a[key].display_name.toUpperCase() : a[key].toUpperCase();
					}
				} 
				return A.includes(filter_UpperCase);
			});
			
			return fliter_Arr;
		},
	}
}
</script>
<style lang="scss" scoped>
.table-head {
	display: flex;
	flex-flow: row nowrap;
	justify-content: flex-start;
	align-items: center;
	height: 48px;
	padding: 0 20px;
	background: #FAFAFA;
	color: #192A3E;
	font-family: Roboto;
	font-style: normal;
	font-weight: normal;
	font-size: 16px;
	line-height: 24px;
	border-bottom: 1px solid #C9C9C9;
	border-radius: 4px 4px 0 0;
	span {
		font-weight: bold;
	}
}
.header-th {
	background: #FAFAFA;
	border-top: 1px solid #C9C9C9;
	&:hover {
		background: #EAEAEA;
		cursor: move;
	}
}
.header-item {
	display: flex;
  flex-flow: row nowrap;
	justify-content: space-between;
  align-items: center;
}
.header-item-sub {
	display: flex;
  flex-flow: row nowrap;
	justify-content: flex-start;
  align-items: center;
}
.header-th:hover .icon {
	visibility: visible; 
}
.header-item-text {
	white-space: nowrap;
	text-overflow: ellipsis;
}
.icon-wrap {
	display: flex;
	flex-flow: row nowrap;
	justify-content: center;
	align-items: center;
}
.icon {
	visibility: hidden;
	width: 14px;
  height: 14px;
	&:hover {
		cursor: pointer;
	}
}
.icon-sort-up-down {
  margin-left: 6px;
}
.search-box {
	padding: 13px;
	display: flex;
	flex-flow: column nowrap;
	justify-content: center;
	align-items: center;
	background: #FFFFFF;
	border-radius: 4px;
	.search-box-button {
		margin-top: 9px;
		display: flex;
		flex-flow: row nowrap;
		justify-content: space-between;
		align-items: center;
		div {
			display: flex;
			flex-flow: row nowrap;
			justify-content: center;
			align-items: center;
			border-radius: 4px;
			padding: 0 21px;

			font-family: Roboto;
			font-style: normal;
			font-weight: 500;
			font-size: 14px;
			height: 36px;

			&:hover {
				cursor: pointer;
			}
		}
		:first-child {
			border: 1px solid rgba(25, 42, 62, 0.7);
			margin-right: 10px;
			color: rgba(25, 42, 62, 0.7);
		}
		:last-child {
			border: 1px solid #00AAFD;
			background: #00AAFD;
			color: #FFFFFF;
		}
	}
}
.filter-box {
	background: #FFFFFF;
}
.text-able {
	color: rgba(0, 0, 0, 0.87);
}
.text-disable {
	color: #808080;
}
.pagination {
	display: flex;
	flex-flow: row nowrap;
	justify-content: flex-start;
	align-items: center;
	.pagination-text {
		font-family: Roboto;
		font-style: normal;
		font-weight: normal;
		font-size: 13px;
		line-height: 16px;
		color: rgba(25, 42, 62, 0.5);
	}
	.pagination-text-page {
		font-family: Roboto;
		font-style: normal;
		font-weight: normal;
		font-size: 13px;
		line-height: 16px;
		color: #192A3E;
		margin: 0 8px;
	}
	.down {
		width: 12px;
		height: 12px;
		margin-top: 10px;
		&:hover {
			cursor: pointer;
		}
	}
}
::v-deep {
	.option-text {
		margin: 0 4px;
		font-family: Roboto;
		font-style: normal;
		font-weight: normal;
		font-size: 13px;
		color: #192A3E;
		&:hover {
			cursor: pointer;
		}
	}
	.v-data-table {
		border-bottom: 1px solid #C9C9C9;
	}
}
</style>